/* global axios */
import ApiClient from './ApiClient';

class QaDashboardApi extends ApiClient {
  constructor() {
    super('qa_dashboard', { accountScoped: true });
  }

  getTemplates() {
    return axios.get(`${this.url}/templates`);
  }

  createTemplate(payload) {
    return axios.post(`${this.url}/templates`, payload);
  }

  updateTemplate(templateId, payload) {
    return axios.patch(`${this.url}/templates/${templateId}`, payload);
  }

  deleteTemplate(templateId) {
    return axios.delete(`${this.url}/templates/${templateId}`);
  }

  getCloseTypeTemplates(locale) {
    return axios.get(`${this.url}/close_type_templates`, {
      params: { locale },
    });
  }

  updateCloseTypeTemplate(payload) {
    return axios.patch(`${this.url}/close_type_templates`, payload);
  }

  getCloseTypeReviews(params = {}) {
    return axios.get(`${this.url}/close_type_reviews`, { params });
  }

  getCloseTypeAudits(params = {}) {
    return axios.get(`${this.url}/close_type_audits`, { params });
  }

  upsertCloseTypeReview(payload) {
    return axios.post(`${this.url}/close_type_reviews`, payload);
  }

  searchKeywords(params = {}) {
    return axios.get(`${this.url}/keyword_searches`, { params });
  }

  exportKeywordSearch(params = {}) {
    return axios.get(`${this.url}/keyword_searches/export`, {
      params,
      responseType: 'blob',
    });
  }

  importTemplatePoliciesFromLegacy() {
    return axios.post(`${this.url}/template_policies/import_legacy`);
  }

  getTemplatePolicies() {
    return axios.get(`${this.url}/template_policies`);
  }

  createTemplatePolicy(payload) {
    return axios.post(`${this.url}/template_policies`, payload);
  }

  updateTemplatePolicy(policyId, payload) {
    return axios.patch(`${this.url}/template_policies/${policyId}`, payload);
  }

  deleteTemplatePolicy(policyId) {
    return axios.delete(`${this.url}/template_policies/${policyId}`);
  }

  publishTemplatePolicy(policyId, payload = {}) {
    return axios.post(
      `${this.url}/template_policies/${policyId}/publish`,
      payload
    );
  }

  rollbackTemplatePolicy(policyId, payload = {}) {
    return axios.post(
      `${this.url}/template_policies/${policyId}/rollback`,
      payload
    );
  }

  syncTemplatePolicyCanned(policyId) {
    return axios.post(`${this.url}/template_policies/${policyId}/sync_canned`);
  }

  getTemplatePolicyVersions(policyId) {
    return axios.get(`${this.url}/template_policies/${policyId}/versions`);
  }

  getTemplateIncidents(params = {}) {
    return axios.get(`${this.url}/template_incidents`, { params });
  }

  updateTemplateIncident(incidentId, payload) {
    return axios.patch(`${this.url}/template_incidents/${incidentId}`, payload);
  }

  getIncidents(params = {}) {
    return axios.get(`${this.url}/incidents`, { params });
  }

  getSummary(params = {}) {
    return axios.get(`${this.url}/summary`, { params });
  }

  getIncident(incidentId) {
    return axios.get(`${this.url}/incidents/${incidentId}`);
  }

  updateIncident(incidentId, payload) {
    return axios.patch(`${this.url}/incidents/${incidentId}`, payload);
  }

  batchReviewIncidents(payload) {
    return axios.post(`${this.url}/incidents/batch_review`, payload);
  }

  getSafetySettings() {
    return axios.get(`${this.url}/safety_settings`);
  }

  updateSafetySettings(payload) {
    return axios.patch(`${this.url}/safety_settings`, payload);
  }

  testSafetySentence(payload) {
    return axios.post(`${this.url}/safety_settings/test_sentence`, payload);
  }

  getSafetyLexiconEntries(params = {}) {
    return axios.get(`${this.url}/safety_lexicon_entries`, { params });
  }

  createSafetyLexiconEntry(payload) {
    return axios.post(`${this.url}/safety_lexicon_entries`, payload);
  }

  updateSafetyLexiconEntry(entryId, payload) {
    return axios.patch(
      `${this.url}/safety_lexicon_entries/${entryId}`,
      payload
    );
  }

  rollbackSafetyLexiconEntry(entryId, version) {
    return axios.patch(`${this.url}/safety_lexicon_entries/${entryId}`, {
      rollback_to_version: version,
    });
  }

  deleteSafetyLexiconEntry(entryId) {
    return axios.delete(`${this.url}/safety_lexicon_entries/${entryId}`);
  }

  bulkUpdateSafetyLexiconEntries(payload) {
    return axios.post(
      `${this.url}/safety_lexicon_entries/bulk_update`,
      payload
    );
  }

  exportSafetyLexicon() {
    return axios.get(`${this.url}/safety_lexicon_entries/export`, {
      responseType: 'blob',
    });
  }

  importSafetyLexiconCsv(payload) {
    return axios.post(`${this.url}/safety_lexicon_entries/import_csv`, payload);
  }

  applySafetyLexiconRecommendation(entryId, payload = {}) {
    return axios.post(
      `${this.url}/safety_lexicon_entries/${entryId}/apply_recommendation`,
      payload
    );
  }

  publishSafetyLexiconEntry(entryId) {
    return axios.post(`${this.url}/safety_lexicon_entries/${entryId}/publish`);
  }

  applySafetyLexiconRecommendations(payload = {}) {
    return axios.post(
      `${this.url}/safety_lexicon_entries/apply_recommendations`,
      payload
    );
  }

  previewSafetyLexiconRecommendations(payload = {}) {
    return axios.post(
      `${this.url}/safety_lexicon_entries/preview_recommendations`,
      payload
    );
  }

  getSafetyIncidents(params = {}) {
    return axios.get(`${this.url}/safety_incidents`, { params });
  }

  updateSafetyIncident(incidentId, payload) {
    return axios.patch(`${this.url}/safety_incidents/${incidentId}`, payload);
  }

  getSafetyFeedbackSummary(params = {}) {
    return axios.get(`${this.url}/safety_feedback_summary`, { params });
  }

  getAlertHub() {
    return axios.get(`${this.url}/alert_hub`);
  }

  updateAlertHub(payload) {
    return axios.patch(`${this.url}/alert_hub`, payload);
  }

  testAlertHubDelivery(payload) {
    return axios.post(`${this.url}/alert_hub/test_delivery`, payload);
  }

  replayAlertHubDelivery(payload) {
    return axios.post(`${this.url}/alert_hub/replay_delivery`, payload);
  }

  simulateAlertHubRoute(payload) {
    return axios.post(`${this.url}/alert_hub/simulate_route`, payload);
  }

  previewAlertHubRoute(payload) {
    return axios.post(`${this.url}/alert_hub/preview_route`, payload);
  }

  getAlertConnections() {
    return axios.get(`${this.url}/alert_connections`);
  }

  createAlertConnection(payload) {
    return axios.post(`${this.url}/alert_connections`, payload);
  }

  updateAlertConnection(connectionId, payload) {
    return axios.patch(
      `${this.url}/alert_connections/${connectionId}`,
      payload
    );
  }

  deleteAlertConnection(connectionId) {
    return axios.delete(`${this.url}/alert_connections/${connectionId}`);
  }

  verifyAlertConnection(connectionId) {
    return axios.post(`${this.url}/alert_connections/${connectionId}/verify`);
  }

  getAlertLogs(params = {}) {
    return axios.get(`${this.url}/alert_logs`, { params });
  }

  getAgentKpis(params = {}) {
    return axios.get(`${this.url}/agent_kpis`, { params });
  }

  // Daily points for the same window: one cheap read of the pre-computed
  // buckets rather than a per-day recompute on the page.
  getAgentKpiSeries(params = {}) {
    return axios.get(`${this.url}/agent_kpis/series`, { params });
  }

  // The SLA episodes behind the first-reply figure. No incident is raised for a
  // reply that merely ran late, so this is the only place those cases exist.
  getAgentKpiEpisodes(params = {}) {
    return axios.get(`${this.url}/agent_kpis/episodes`, { params });
  }

  computeAgentKpiSnapshot(params = {}) {
    return axios.post(`${this.url}/agent_kpis/compute_snapshot`, params);
  }

  getCustomerInsight(params = {}) {
    return axios.get(`${this.url}/customer_insight`, { params });
  }

  getCustomerInsightCapabilities() {
    return axios.get(`${this.url}/customer_insight/capabilities`);
  }

  getCalibration(params = {}) {
    return axios.get(`${this.url}/calibration`, { params });
  }

  getTrends(params = {}) {
    return axios.get(`${this.url}/trends`, { params });
  }

  getAppeals(params = {}) {
    return axios.get(`${this.url}/appeals`, { params });
  }

  createAppeal(payload) {
    return axios.post(`${this.url}/appeals`, payload);
  }

  approveAppeal(appealId, payload = {}) {
    return axios.post(`${this.url}/appeals/${appealId}/approve`, payload);
  }

  denyAppeal(appealId, payload = {}) {
    return axios.post(`${this.url}/appeals/${appealId}/deny`, payload);
  }

  getDailyAuditSettings() {
    return axios.get(`${this.url}/daily_audit_settings`);
  }

  updateDailyAuditSettings(payload) {
    return axios.patch(`${this.url}/daily_audit_settings`, payload);
  }
}

export default new QaDashboardApi();
